import React, { useEffect, useRef, useState } from 'react';
import Toggle from './Toggle';
import SearchViewSelect from './SearchViewSelect';
import { useCompatibleSearchViews } from '../hooks/useSearchViews';
import { loadLastCustomSearch, saveLastCustomSearch } from '../utils/lastCustomSearch';
import {
  CUSTOM_SEARCH_FIELDS,
  DEFAULT_CUSTOM_SEARCH_FIELD,
  splitCustomSearchValues,
} from '../utils/customOrderSearch';

/* Body of the header's Custom Search modal — the V1 "Custom Search" window
 * (lib_search.pl docustomsearchwin, ~3226) laid out like the Quotes page's
 * CustomSearchDialog.
 *
 * NOTE on "View": this picker is NOT V1's view-type picker, which is still not
 * ported. V1's is `searchdisplay` (defs.pl ~3580) — a fixed ACL-gated list of
 * display GRAINS (Order / Container Mgmt / Invoice / Invoice Line Item / Addl
 * Cost / Driver Pay LI / Scheduling) that changes the row grain and joins. This
 * one selects a manager-published LAYOUT for the results grid; the two happen to
 * share a word.
 *
 * The modal chrome (drag, close) belongs to DraggableModal; this owns the inputs
 * and hands the parsed values up via onSearch. */

interface Props {
  /** Fires with the split/cleaned values — never with an empty list. */
  onSearch: (searchBy: string, terms: string[], includeDeleted: boolean, viewId: string) => void;
  onCancel: () => void;
  /** Remounts each open with the last submitted search; focuses like V1 does. */
  autoFocus?: boolean;
  /** Preselected View id, so reopening the modal from a results view keeps it. */
  initialViewId?: string;
  /**
   * Receives the Search action so the modal's owner can bind Alt+S to it — the
   * hotkey layer belongs to the DraggableModal shell's parent, not to this form
   * (see GlobalSearch). Set to null whenever the button is disabled, so the chord
   * inherits the button's gating instead of re-deriving it.
   */
  submitRef?: React.MutableRefObject<(() => void) | null>;
}

const CustomOrderSearchForm: React.FC<Props> = ({
  onSearch,
  onCancel,
  autoFocus = true,
  initialViewId,
  submitRef,
}) => {
  const [lastSearch] = useState(loadLastCustomSearch);
  const [searchBy, setSearchBy] = useState(lastSearch?.searchBy ?? DEFAULT_CUSTOM_SEARCH_FIELD);
  const [text, setText] = useState(lastSearch?.text ?? '');
  const [includeDeleted, setIncludeDeleted] = useState(lastSearch?.includeDeleted ?? false);
  const [viewId, setViewId] = useState(initialViewId ?? lastSearch?.viewId ?? '');
  const { views, myViews } = useCompatibleSearchViews();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // V1 focuses + selects the paste box on open, so a re-paste replaces the old list.
  useEffect(() => {
    if (!autoFocus) return;
    textareaRef.current?.focus();
    textareaRef.current?.select();
  }, [autoFocus]);

  // Parsed with the field's own rule, so the count reflects what will actually be
  // searched (LOT NUMBER keeps spaces/commas; everything else splits on them).
  const terms = splitCustomSearchValues(searchBy, text);

  const submit = () => {
    if (!terms.length) return;
    // Save only submitted inputs, so Cancel/close cannot replace the last search.
    saveLastCustomSearch({ searchBy, text, includeDeleted, viewId });
    onSearch(searchBy, terms, includeDeleted, viewId);
  };

  // Republish on every render (no dep array): `submit` closes over the current
  // values, and the null/callable swap has to track the button's disabled state
  // keystroke by keystroke.
  useEffect(() => {
    if (!submitRef) return;
    submitRef.current = terms.length ? submit : null;
    return () => {
      submitRef.current = null;
    };
  });

  // Ctrl/Cmd+Enter searches from inside the textarea, where Enter means newline.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  const inputCls =
    'border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-[0.75rem] bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[0.75rem] text-slate-500 dark:text-slate-400">
          Paste values — one per line or comma-separated — then choose what to search by.
        </p>
        <button
          type="button"
          onClick={() => {
            setText('');
            textareaRef.current?.focus();
          }}
          disabled={!text.length}
          className="shrink-0 px-3 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-40 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
        >
          Clear All
        </button>
      </div>
      <textarea
        ref={textareaRef}
        aria-label="Search values"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={12}
        className={`${inputCls} w-full resize-y`}
        placeholder={'1050\n1051\n1052'}
      />
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[0.75rem] font-semibold text-slate-600 dark:text-slate-300">Search By:</span>
        <select
          aria-label="Search By"
          className={inputCls}
          value={searchBy}
          onChange={(e) => setSearchBy(e.target.value)}
        >
          {CUSTOM_SEARCH_FIELDS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        {/* V1's `searchactive` filter: off restricts to deleted='N'. Deleted orders
            that do come back are red-highlighted by the results grid. */}
        <label className="flex items-center gap-1.5 text-[0.75rem] font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none">
          <Toggle
            size="sm"
            checked={includeDeleted}
            onChange={setIncludeDeleted}
            ariaLabel="Include deleted orders"
          />
          Include Deleted
        </label>
        {/* Renders nothing until a manager has published a View. */}
        <SearchViewSelect
          views={views}
          myViews={myViews}
          value={viewId}
          onChange={setViewId}
          className={inputCls}
        />
        {terms.length > 0 && (
          <span className="text-[0.75rem] text-slate-400 dark:text-slate-500">
            {terms.length} {terms.length === 1 ? 'value' : 'values'}
          </span>
        )}
      </div>
      <div className="mt-1 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700 pt-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!terms.length}
          className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded hover:bg-blue-700 disabled:opacity-40"
        >
          {/* Same `[letter]` hint the PRO tabs use, lightened for the blue fill.
              aria-hidden so the accessible name stays plain "Search" — the chord
              is a mouse-user affordance, and screen readers get no value from the
              brackets. */}
          <span className="mr-1.5 text-[0.625rem] font-medium text-white/70" aria-hidden="true">
            [S]
          </span>
          Search
        </button>
      </div>
    </div>
  );
};

export default CustomOrderSearchForm;
